import React, { createContext, useState, ReactNode, useEffect, useContext, useMemo } from 'react';
import { db } from '../firebase';
import { collection, query, onSnapshot, orderBy, doc, addDoc, updateDoc, deleteDoc, where, getDocs, writeBatch, Timestamp, setDoc } from 'firebase/firestore';
import { useAuth } from '../hooks/useAuth';
import { useSantri } from './SantriContext';
import { useUserAccess } from './UserAccessContext';
import { AbsensiLiburan, NewAbsensiLiburanData, AbsenRecord, NewAbsenRecordData, SyaratLiburan, NewSyaratLiburanData } from '../types';

export interface AbsensiLiburanContextType {
    loading: boolean;
    absensiLiburan: AbsensiLiburan[];
    absenRecords: AbsenRecord[];
    syaratLiburan: SyaratLiburan[];
    lastActiveAbsensiId: string | undefined;
    activeAbsensiLiburan: AbsensiLiburan | undefined;
    activeAbsensiKedatangan: AbsensiLiburan | undefined;

    addAbsensiLiburan: (data: NewAbsensiLiburanData) => Promise<any>;
    updateAbsensiLiburan: (id: string, data: Partial<NewAbsensiLiburanData>) => Promise<void>;
    deleteAbsensiLiburan: (id: string) => Promise<void>;
    setActiveAbsensiLiburan: (id: string) => Promise<void>;
    finalizeAbsensiLiburan: () => Promise<{ success: number; failed: number }>;

    fetchAbsenRecordsByEventId: (absensiId: string) => Promise<AbsenRecord[]>;
    addAbsenRecord: (data: NewAbsenRecordData) => Promise<void>;
    updateAbsenRecord: (id: string, data: Partial<NewAbsenRecordData>) => Promise<void>;
    deleteAbsenRecord: (id: string) => Promise<void>;
    deleteMultipleAbsenRecords: (ids: string[]) => Promise<void>;
    getSantriAbsenceHistory: (santriId: string) => Promise<AbsenRecord[]>;
    cekStatusAbsensiLiburanAktif: (santriId: string) => Promise<AbsenRecord | null>;

    addSyaratLiburan: (data: NewSyaratLiburanData) => Promise<any>;
    updateSyaratLiburan: (id: string, data: Partial<NewSyaratLiburanData>) => Promise<void>;
    deleteSyaratLiburan: (id: string) => Promise<void>;
}

export const AbsensiLiburanContext = createContext<AbsensiLiburanContextType | undefined>(undefined);

export const useAbsensiLiburan = () => {
    const context = useContext(AbsensiLiburanContext);
    if (context === undefined) {
        throw new Error('useAbsensiLiburan must be used within an AbsensiLiburanProvider');
    }
    return context;
};

export const AbsensiLiburanProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { authUser } = useAuth();
    const { santri } = useSantri();
    const { isSuperAdmin, userPermissions } = useUserAccess();
    const currentUser = sessionStorage.getItem('currentUser');

    const hasAbsensiAccess = useMemo(() => {
        if (!authUser) return false;
        if (isSuperAdmin) return true;
        const permData = userPermissions?.find(p => p.email === authUser.email);
        const allowedMenus = permData?.allowedMenus || [];
        return allowedMenus.some(menu => ['pg-kepala-kesantrian', 'pg-murobbi'].includes(menu));
    }, [authUser, isSuperAdmin, userPermissions]);

    const [absensiLiburan, setAbsensiLiburan] = useState<AbsensiLiburan[]>([]);
    const [absenRecords, setAbsenRecords] = useState<AbsenRecord[]>([]);
    const [syaratLiburan, setSyaratLiburan] = useState<SyaratLiburan[]>([]);
    const [lastActiveAbsensiId, setLastActiveAbsensiId] = useState<string | undefined>(undefined);
    const [loading, setLoading] = useState(true);

    const activeAbsensi = useMemo(() => absensiLiburan.find(a => a.isActive), [absensiLiburan]);
    const activeAbsensiLiburan = useMemo(() => activeAbsensi?.jenis === 'Liburan' ? activeAbsensi : undefined, [activeAbsensi]);
    const activeAbsensiKedatangan = useMemo(() => activeAbsensi?.jenis === 'Kedatangan' ? activeAbsensi : undefined, [activeAbsensi]);

    useEffect(() => {
        if (!authUser) {
            setAbsensiLiburan([]);
            setAbsenRecords([]);
            setSyaratLiburan([]);
            setLoading(false);
            return;
        }

        setLoading(true);

        const absensiSettingsDocRef = doc(db, 'settings', 'absensi');
        const unsubscribeAbsensiSettings = onSnapshot(absensiSettingsDocRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                if (typeof data.lastActiveId === 'string') {
                    setLastActiveAbsensiId(data.lastActiveId);
                }
            }
        });

        const absensiLiburanQuery = query(collection(db, 'absensi_liburan'), orderBy('tanggal', 'desc'));
        const unsubscribeAbsensiLiburan = onSnapshot(absensiLiburanQuery, snapshot => {
            const absensiData = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    tanggal: (data.tanggal as Timestamp).toDate(),
                } as AbsensiLiburan;
            });
            setAbsensiLiburan(absensiData);
            setLoading(false);
        });

        const fetchSyaratLiburan = async () => {
            try {
                const syaratLiburanQuery = query(collection(db, 'syarat_liburan'), orderBy('namaSyarat', 'asc'));
                const snapshot = await getDocs(syaratLiburanQuery);
                const syaratData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as SyaratLiburan));
                setSyaratLiburan(syaratData);
            } catch (e) { console.error(e); }
        };
        fetchSyaratLiburan();

        return () => {
            unsubscribeAbsensiSettings();
            unsubscribeAbsensiLiburan();
        };
    }, [authUser]);

    // Special Subscription for Absen Records (Liburan/Kedatangan) depending on active event
    useEffect(() => {
        let isMounted = true;
        if (!authUser || !hasAbsensiAccess) {
            if (isMounted) setAbsenRecords([]);
            return;
        }

        let q;
        if (activeAbsensi) {
            q = query(
                collection(db, 'absen_records'),
                where('absensiId', '==', activeAbsensi.id)
            );
        } else {
            const twoDaysAgo = new Date();
            twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);
            q = query(
                collection(db, 'absen_records'),
                where('tanggalAbsen', '>=', twoDaysAgo),
                orderBy('tanggalAbsen', 'desc')
            );
        }

        const unsubscribe = onSnapshot(q, snapshot => {
            const records = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    tanggalAbsen: (data.tanggalAbsen as Timestamp).toDate(),
                } as AbsenRecord;
            });
            records.sort((a, b) => b.tanggalAbsen.getTime() - a.tanggalAbsen.getTime());
            setAbsenRecords(records);
        });

        return () => unsubscribe();
    }, [authUser, activeAbsensi?.id]);

    const addAbsensiLiburan = async (data: NewAbsensiLiburanData): Promise<any> => await addDoc(collection(db, 'absensi_liburan'), { ...data, isActive: false });
    const updateAbsensiLiburan = async (id: string, data: Partial<NewAbsensiLiburanData>) => await updateDoc(doc(db, 'absensi_liburan', id), data);
    const deleteAbsensiLiburan = async (id: string) => await deleteDoc(doc(db, 'absensi_liburan', id));

    const setActiveAbsensiLiburan = async (id: string) => {
        const batch = writeBatch(db);
        const q = query(collection(db, 'absensi_liburan'));
        const snapshot = await getDocs(q);

        snapshot.forEach(doc => {
            batch.update(doc.ref, { isActive: false });
        });

        const docToActivate = snapshot.docs.find(d => d.id === id);
        const isActivating = docToActivate && !docToActivate.data().isActive;

        if (isActivating) {
           batch.update(doc(db, 'absensi_liburan', id), { isActive: true });
           batch.set(doc(db, 'settings', 'absensi'), { lastActiveId: id }, { merge: true });
        }

        await batch.commit();
    };

    const fetchAbsenRecordsByEventId = async (absensiId: string): Promise<AbsenRecord[]> => {
        const q = query(collection(db, 'absen_records'), where('absensiId', '==', absensiId));
        const snap = await getDocs(q);
        return snap.docs.map(doc => {
            const data = doc.data();
            return {
                id: doc.id,
                ...data,
                tanggalAbsen: (data.tanggalAbsen as Timestamp).toDate(),
            } as AbsenRecord;
        });
    };

    const finalizeAbsensiLiburan = async (): Promise<{ success: number; failed: number }> => {
        const activeEvent = activeAbsensiLiburan || activeAbsensiKedatangan;
        if (!activeEvent) {
          throw new Error("Tidak ada absensi yang sedang aktif.");
        }

        const activeSantri = santri.filter(s => s.status === 'Aktif');
        const existingRecordSantriIds = new Set(
          absenRecords
            .filter(r => r.absensiId === activeEvent.id)
            .map(r => r.santriId)
        );

        const missingSantri = activeSantri.filter(s => !existingRecordSantriIds.has(s.id));

        if (missingSantri.length === 0) {
          return { success: 0, failed: 0 };
        }

        const batchSize = 450;
        let successCount = 0;

        for (let i = 0; i < missingSantri.length; i += batchSize) {
            const chunk = missingSantri.slice(i, i + batchSize);
            const batch = writeBatch(db);

            chunk.forEach(s => {
                const newRecordRef = doc(collection(db, 'absen_records'));
                const recordData: NewAbsenRecordData = {
                    santriId: s.id,
                    santriName: s.nama,
                    kelas: s.kelas,
                    asrama: s.asrama,
                    absensiId: activeEvent.id,
                    absensiName: activeEvent.nama,
                    jenisAbsen: activeEvent.jenis,
                    tanggalAbsen: new Date(),
                    jamAbsen: new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }).replace('.', ':'),
                    petugasName: currentUser || 'System Auto',
                    Status: 'Tidak Laporan',
                    keterangan: 'Tidak Laporan Presensi'
                };
                batch.set(newRecordRef, recordData);
                successCount++;
            });

            await batch.commit();
        }

        return { success: successCount, failed: 0 };
    };

    const addAbsenRecord = async (data: NewAbsenRecordData): Promise<void> => {
        const tempId = `temp-${Date.now()}`;
        const newRecord: AbsenRecord = { ...data, id: tempId };
        setAbsenRecords(prevRecords => [newRecord, ...prevRecords]);

        try {
            await addDoc(collection(db, 'absen_records'), data);
        } catch (error) {
            console.error("Failed to add absen record: ", error);
            setAbsenRecords(prevRecords => prevRecords.filter(record => record.id !== tempId));
            throw error;
        }
    };

    const updateAbsenRecord = async (id: string, data: Partial<NewAbsenRecordData>) => await updateDoc(doc(db, 'absen_records', id), data);
    const deleteAbsenRecord = async (id: string) => await deleteDoc(doc(db, 'absen_records', id));

    const deleteMultipleAbsenRecords = async (ids: string[]) => {
        if (ids.length === 0) return;
        const batch = writeBatch(db);
        ids.forEach(id => batch.delete(doc(db, 'absen_records', id)));
        await batch.commit();
    };

    const cekStatusAbsensiLiburanAktif = async (santriId: string): Promise<AbsenRecord | null> => {
        if (absenRecords.length > 0 && activeAbsensi) {
            const memoryRecord = absenRecords.find(r => r.santriId === santriId && r.absensiId === activeAbsensi.id);
            if (memoryRecord) return memoryRecord;
        }

        if (!activeAbsensi) return null;

        const q = query(
            collection(db, 'absen_records'),
            where('santriId', '==', santriId),
            where('absensiId', '==', activeAbsensi.id)
        );
        const snapshot = await getDocs(q);
        if (snapshot.empty) return null;

        const docSnap = snapshot.docs[0];
        const data = docSnap.data();
        return {
            id: docSnap.id,
            ...data,
            tanggalAbsen: (data.tanggalAbsen as Timestamp).toDate(),
        } as AbsenRecord;
    };

    const getSantriAbsenceHistory = async (santriId: string): Promise<AbsenRecord[]> => {
        const q = query(
            collection(db, 'absen_records'),
            where('santriId', '==', santriId),
            orderBy('tanggalAbsen', 'desc')
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => {
            const data = doc.data();
            return {
                id: doc.id,
                ...data,
                tanggalAbsen: (data.tanggalAbsen as Timestamp).toDate(),
            } as AbsenRecord;
        });
    };

    const addSyaratLiburan = async (data: NewSyaratLiburanData): Promise<any> => await addDoc(collection(db, 'syarat_liburan'), data);
    const updateSyaratLiburan = async (id: string, data: Partial<NewSyaratLiburanData>) => await updateDoc(doc(db, 'syarat_liburan', id), data);
    const deleteSyaratLiburan = async (id: string) => await deleteDoc(doc(db, 'syarat_liburan', id));

    return (
        <AbsensiLiburanContext.Provider value={{
            loading,
            absensiLiburan, absenRecords, syaratLiburan,
            lastActiveAbsensiId, activeAbsensiLiburan, activeAbsensiKedatangan,
            addAbsensiLiburan, updateAbsensiLiburan, deleteAbsensiLiburan, setActiveAbsensiLiburan, finalizeAbsensiLiburan,
            fetchAbsenRecordsByEventId, addAbsenRecord, updateAbsenRecord, deleteAbsenRecord, deleteMultipleAbsenRecords,
            getSantriAbsenceHistory, cekStatusAbsensiLiburanAktif,
            addSyaratLiburan, updateSyaratLiburan, deleteSyaratLiburan
        }}>
            {children}
        </AbsensiLiburanContext.Provider>
    );
};
